import { EnhancedCouponData } from "@/types/coupon-wall";
import { PoiTagType, PoiTagInfo, EnhancedPoi, Poi, CategoryType, Sku, PoiCoupon, EnhancedPoiCoupon, CouponSource } from "@/types/poiCard";
import { removeImageScheme } from "../utils";
import { formatYuan } from "../price";

// 满减标签最多显示的多少个
const CUT_LABEL_MAX_COUNT = 3;
// Poi 标签类型映射
const TagTypeMap: Record<PoiTagType, string> = {
  [PoiTagType.RANK]: "rankLabel", // 排名
  [PoiTagType.DIANPING]: "dianpingLabel", // 点评
  [PoiTagType.MAGIC_COUPON]: "magicCouponLabel", // 神券
  [PoiTagType.CUT]: "cutLabels", // 满减
  [PoiTagType.SHIP]: "shipIcon", // 配送
  [PoiTagType.WAIMAI_CATEGORY]: "categoryLabel", // 品类标签
};

// 格式化韦恩 Poi 的标签数据
function formatPoiLabel(tagInfoList: PoiTagInfo[]) {
  const ret = {
    rankLabel: "", // 排名标签
    dianpingLabel: "", // 点评标签
    magicCouponLabel: "", // 神券标签
    cutLabels: [] as string[], // 满减标签
    shipIcon: "", //  配送标签
    categoryLabel: { // 品类标签
      type: null as CategoryType | null,
      label: "",
    },
  };

  if (Array.isArray(tagInfoList) && tagInfoList.length > 0) {
    tagInfoList.forEach((item) => {
      if (item.type in TagTypeMap && item.text) {
        if (item.type === PoiTagType.CUT) {
          if (ret.cutLabels.length < CUT_LABEL_MAX_COUNT) {
            ret.cutLabels.push(item.text);
          }
        } else if ([PoiTagType.WAIMAI_CATEGORY].includes(item.type)) {
          ret.categoryLabel.type = item.type as unknown as CategoryType;
          ret.categoryLabel.label = item.text;
        } else {
          ret[TagTypeMap[item.type] as keyof Omit<typeof ret, "cutLabels" | "categoryLabel">] = item.text;
        }
      }
    });
  }
  return ret;
}

// 格式化下挂菜信息
function formatSkuInfo(skuData: Sku): Sku {
  return {
    ...skuData,
    // 商品头图移除协议头，使用页面协议。背景：接口下发的图片都是 http 协议，在鸿蒙、微信环境不支持展示
    headImage: removeImageScheme(skuData?.headImage),
  };
}

// 格式化韦恩 Poi 数据
export function formatPoiInfo(poiData: Poi): EnhancedPoi {
  const { categoryLabel, rankLabel, dianpingLabel, magicCouponLabel, cutLabels, shipIcon } = formatPoiLabel(
    poiData.tagInfoList
  );

  return {
    // 基础信息(原始字段)
    title: poiData.title, // 商家名称
    categoryLabel, // 品类标签
    headImage: removeImageScheme(poiData.headImage), // 商家头图
    score: poiData.score, // 评分
    monthSaleNum: poiData.monthSaleNum, // 月售
    minPrice: poiData.poiPrice.minPrice, // 起送价格
    shippingFee: poiData.poiPrice.shippingFee, // 配送费
    avgDeliveryTime: poiData.avgDeliveryTime, // 配送时间
    distance: poiData.distance, // 配送距离距离
    poiIdStr: poiData.poiIdStr,

    // 优惠信息（前端转换）
    rankLabel, // 排名标签
    dianpingLabel, // 点评标签
    magicCouponLabel, // 神券标签
    cutLabels, // 满减标签
    shipIcon, // 配送图标
    coupon: poiData.coupon ? handlePoiCoupon(poiData.coupon) : null, // 券信息

    // 跳转参数
    operation: poiData.operation,
    // 下挂菜
    skuList: Array.isArray(poiData.skuList) && poiData.skuList.length > 0
      ? poiData.skuList.map(formatSkuInfo)
      : [],
  };
}

// 在处理后的 Poi 数据中添加下挂券信息
export function addCouponInfo(poiData: EnhancedPoi, coupon?: EnhancedCouponData | null): EnhancedPoi {
  return {
    ...poiData,
    coupon: {
      ...coupon,
      // 标识商卡下挂券数据来自券快筛，用户选中券快筛可使用缓存数据时，需要使用选中的券快筛数据覆盖
      // 因为有可能多个券快筛复用相同的韦恩 ID，缓存可以在多个券快筛之间复用
      couponSource: CouponSource.COUPON_SELECTION,
    } as EnhancedPoiCoupon,
  }
}

export function handlePoiCoupon(coupon: PoiCoupon): EnhancedPoiCoupon {
  const { value: couponValue, minMoney: priceLimit, timeDesc } = coupon;
  const priceLimitYuan = formatYuan(priceLimit);
  const couponValueYuan = formatYuan(couponValue);

  return {
    ...coupon,
    couponValue: couponValueYuan,
    priceLimit: priceLimitYuan,
    couponEndTime: timeDesc, // TODO 本期没有倒计时功能，接口返回情况不确定
    actSysSrc: '', // 接口获取不到，埋点上报 ''
    couponSource: CouponSource.RECALL, // 标识由券平台召回，接口下发，用户选中券快筛，可使用缓存数据时，不用选中的券快筛数据覆盖
  }
}